import React from 'react';
// import { text as mpvId, text, translatedUrlUtil, text as url } from '@vp/dst-localization-util';
import { Box } from '@vp/swan';
import { useIdentityContext } from '@99designs/design-services-common';
import { __, __product, __url, __vistaRoute, getCurrentCulture } from '@99designs/i18n';
import { useIsBifrostEnabled } from '../RegionLockedConfiguration';
import FeaturedCard from './FeaturedCard';
import ModalSubheader from './ModalSubheader';

export interface DestinationPair {
    url: string;
    pageName: string;
}

export function useEssentialLogoPackageDestination(): DestinationPair {
    const { anonymousUserId } = useIdentityContext();
    const currentCulture = getCurrentCulture();
    const isBifrostEnabled = useIsBifrostEnabled(currentCulture);
    if (isBifrostEnabled) {
        return {
            url: __url('/experts/services/details/logoessential'),
            pageName: 'logo-essential:Product Page',
        };
    } else {
        const ninetyNineUrl = __url('/logo-design');
        const param = `vistaprint_aid=${encodeURIComponent(anonymousUserId)}`;
        const separator = ninetyNineUrl.includes('?') ? '&' : '?';

        return {
            url: `${ninetyNineUrl}${separator}${param}`,
            pageName: '99designs-',
        };
    }
}

export function useBriefDestination(mpvId: string): DestinationPair {
    return {
        url: `${__vistaRoute('/design/briefs/create/care-design')}?mpvId=${mpvId}`,
        pageName: 'Design Services:Expert Services Brief',
    };
}

export function useLogoBrandIdentityPackDestination(): DestinationPair {
    const { anonymousUserId } = useIdentityContext();
    return {
        url: `${__url(
            '99designs.com'
        )}/brand-identity-pack/details?vistaprint_aid=${anonymousUserId}`,
        pageName: '99designs-',
    };
}

export default function FeaturedCards() {
    const { url: businessCardUrl, pageName: businessCardPageName } = useBriefDestination(
        __product('mpv-id-business-card')
    );
    const { url: essentialLogoPackageUrl, pageName: essentialLogoPackagePageName } =
        useEssentialLogoPackageDestination();
    const { url: flyersUrl, pageName: flyersPageName } = useBriefDestination(
        __product('mpv-id-flyer')
    );
    const { url: logoBrandIdentityPackUrl, pageName: logoBrandIdentityPackPageName } =
        useLogoBrandIdentityPackDestination();

    return (
        <Box marginBottom={'6'}>
            <ModalSubheader>{__('Featured')}</ModalSubheader>
            <FeaturedCard
                destinationUrl={businessCardUrl}
                destinationPageName={businessCardPageName}
                image="https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/expert_services/Dashboard/FeatureCards/business-card-design.png"
                title={__('Business card design')}
                subtitle={__('Custom, budget-friendly design help')}
                position={1}
            />
            <FeaturedCard
                destinationUrl={essentialLogoPackageUrl}
                destinationPageName={essentialLogoPackagePageName}
                image="https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/expert_services/Dashboard/FeatureCards/essential-logo-package.png"
                title={__('Essential logo package')}
                subtitle={__('Pick a designer to create a logo design as unique as your business')}
                position={2}
            />
            <FeaturedCard
                destinationUrl={flyersUrl}
                destinationPageName={flyersPageName}
                image="https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/expert_services/Dashboard/FeatureCards/flyer-design.png"
                title={__('Flyer design')}
                subtitle={__('Give your flyer a professional finish')}
                position={3}
            />
            <FeaturedCard
                destinationUrl={logoBrandIdentityPackUrl}
                destinationPageName={logoBrandIdentityPackPageName}
                image="https://cms.cloudinary.vpsvc.com/image/upload/f_auto,q_auto/expert_services/Dashboard/FeatureCards/logo-brand-identity-pack.png"
                title={__('Logo & brand identity pack')}
                subtitle={__('Bring your brand to life, starting with a logo design contest')}
                position={4}
            />
        </Box>
    );
}
